import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
}

export async function uploadExperiencePhoto(file) {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await axios.post(`${API}/experiences/photo`, formData, {
    headers: authHeaders(),
  });
  return data; // { photo_url }
}

export async function createExperience(payload) {
  const { data } = await axios.post(`${API}/experiences`, payload, {
    headers: authHeaders(),
  });
  return data;
}

export async function fetchMyExperiences() {
  const { data } = await axios.get(`${API}/experiences/mine`, {
    headers: authHeaders(),
  });
  return data;
}

export async function deleteMyExperience(id) {
  const { data } = await axios.delete(`${API}/experiences/${id}`, {
    headers: authHeaders(),
  });
  return data;
}

/**
 * Convert a What3Words address (e.g. "lemon.tiger.rope" or
 * "///lemon.tiger.rope") into coordinates. Returns null if the address
 * is invalid or the backend has no What3Words API key configured.
 */
export async function what3wordsToCoords(address) {
  const cleaned = (address || '').replace(/^\/\/\//, '').trim();
  if (!cleaned) return null;
  try {
    const { data } = await axios.post(
      `${API}/experiences/what3words/to-coords`,
      { words: cleaned },
      { headers: authHeaders() },
    );
    return data; // { latitude, longitude, words } or null
  } catch (err) {
    if (err.response?.status === 404 || err.response?.status === 400) return null;
    throw err;
  }
}

/**
 * Public — approved experiences for the guest map. No auth required.
 * Requires the caller's live position: the backend filters to a hard-
 * coded 20 km radius around { lat, lng } so we don't ship every pin
 * on the planet. When lat/lng are missing the backend returns [],
 * which is the safe default for guests who haven't granted location.
 * When the caller is signed in, each row also carries their own
 * `my_reaction` ("like" | "dislike" | null) so the like/dislike UI
 * can render its toggle state without a second round-trip.
 * Returns array of { id, latitude, longitude, category, tags, description,
 * photo_url, tier, listing_id, host_name, created_at, approved_at,
 * like_count, dislike_count, net_likes, my_reaction }.
 */
export async function fetchApprovedExperiences({ lat, lng } = {}) {
  const params = {};
  if (typeof lat === 'number' && typeof lng === 'number') {
    params.lat = lat;
    params.lng = lng;
  }
  // Send the token when we have one so the backend can attach the
  // guest's own reaction per row. The endpoint stays public for
  // signed-out callers — a missing/invalid token is silently ignored.
  const token = typeof window !== 'undefined' ? window.localStorage.getItem('token') : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const { data } = await axios.get(`${API}/experiences/approved`, { params, headers });
  return data;
}

/**
 * Guest thumb-up / thumb-down on an approved experience.
 * `value` is `"like"`, `"dislike"`, or `null` to clear. Returns the
 * updated `{ like_count, dislike_count, net_likes, my_reaction }`.
 */
export async function reactToExperience(id, value) {
  const { data } = await axios.post(
    `${API}/experiences/${id}/reaction`,
    { value: value || null },
    { headers: authHeaders() },
  );
  return data;
}

/**
 * Get the host's listing coordinates (used to center the map when the
 * host opens the add-experience flow). Falls back to a first-listing
 * lookup if `listing_id` is not supplied.
 */
export async function fetchHostListingCenter() {
  const { data } = await axios.get(`${API}/experiences/host-center`, {
    headers: authHeaders(),
  });
  return data; // { latitude, longitude, listing_id, listing_title }
}
